// ProductionCalendar.js

import React, { useState, useMemo, useEffect, useRef } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import axios from "axios";
import { appendProductionInstanceScope } from '../productionRequestScope';
import "react-big-calendar/lib/css/react-big-calendar.css";

import JobCardModal from "./JobCardModal";
const scopedProductionUrl = (input) => appendProductionInstanceScope(input, {
  pathname: window.location.pathname, apiBase: process.env.REACT_APP_NODEJS_SERVER_URL,
});

const localizer = momentLocalizer(moment);

const PALETTE = {
  accent: "#2563EB",
  slate: "#475569",
  border: "#E6EEF6",
  bg: "#FFFFFF",
  late: "#EF4444",
  completed: "#16A34A",
  neutral: "#94A3B8",
};

const STATUS_COLORS = {
  Scheduled: "#64748B",
  InProduction: PALETTE.accent,
  QC: "#F59E0B",
  Completed: PALETTE.completed,
  Default: PALETTE.neutral,
};

const getStatusColor = (s) => STATUS_COLORS[s] || STATUS_COLORS.Default;

const INJECTED_CSS = `

.ppc-root { font-family: Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial; padding:16px; color:${PALETTE.slate}; }
.ppc-header { display:flex; justify-content:space-between; align-items:center; gap:12px; margin-bottom:12px; }
.ppc-title { font-size:1.1rem; font-weight:600; color:#0f172a; display:flex; gap:10px; align-items:center; }
.ppc-actions { display:flex; gap:8px; align-items:center; }

/* Stats */
.ppc-stats { display:flex; gap:8px; align-items:center; }
.ppc-stat { background:${PALETTE.bg}; border:1px solid ${PALETTE.border}; border-radius:8px; padding:8px 10px; min-width:80px; text-align:center; box-shadow:0 1px 6px rgba(2,6,23,0.04); }
.ppc-stat .value { font-weight:700; font-size:1rem; color:#0f172a; }

/* Filters */
.ppc-controls { display:flex; gap:8px; align-items:center; flex-wrap:wrap; margin-bottom:10px; }
.ppc-search { width:220px; }
.ppc-btn { border-radius:8px; }

/* Calendar wrapper */
.ppc-calendar-wrap { background:#f8fafc; padding:12px; border-radius:10px; box-shadow: 0 6px 30px rgba(2,6,23,0.04); }

/* Event card minimal */
.ppc-event { display:flex; gap:8px; align-items:flex-start; padding:6px; background:${PALETTE.bg}; border-radius:6px; border:1px solid ${PALETTE.border}; box-shadow:none; height:100%; overflow:hidden; }
.ppc-event-accent { width:6px; border-radius:4px; flex:0 0 6px; margin-top:4px; }
.ppc-event-main { min-width:0; flex:1; }
.ppc-event-title { display:flex; justify-content:space-between; align-items:center; gap:6px; font-size:13px; font-weight:600; color:#0f172a; }
.ppc-event-sub { font-size:12px; color:${PALETTE.slate}; margin-top:4px; display:flex; justify-content:space-between; gap:8px; align-items:center; }
.ppc-event-progress { margin-top:8px; display:flex; align-items:center; gap:8px; }
.ppc-track { flex:1; height:8px; background:#eef6ff; border-radius:6px; overflow:hidden; }
.ppc-fill { height:100%; border-radius:6px; transition:width 220ms ease; }

/* Day header circular */
.ppc-day-header { display:flex; align-items:center; gap:8px; padding:6px; }
.ppc-day-indicator { width:36px; height:36px; display:flex; align-items:center; justify-content:center; }

/* Hover tooltip */
.ppc-tooltip { position:fixed; z-index:1500; min-width:240px; max-width:360px; background:${PALETTE.bg}; border:1px solid ${PALETTE.border}; border-radius:8px; padding:10px; box-shadow:0 12px 40px rgba(2,6,23,0.12); font-size:13px; color:${PALETTE.slate}; }

/* Slide-over panel */
.ppc-slide { position:fixed; right:18px; top:72px; width:380px; max-width:92vw; bottom:18px; background:${PALETTE.bg}; border-radius:12px; padding:14px; box-shadow: 0 18px 60px rgba(2,6,23,0.14); z-index:2000; overflow:auto; }

/* small helpers */
.badge-pri { font-size:11px; padding:4px 8px; border-radius:999px; border:1px solid ${PALETTE.border}; }
.ppc-more-indicator { font-size:12px; color:${PALETTE.slate}; padding:6px; }

/* responsive */
@media (max-width:900px) {
  .ppc-header { flex-direction:column; align-items:stretch; gap:8px; }
  .ppc-stats { width:100%; justify-content:space-between; }
  .ppc-slide { right:8px; top:64px; width:94vw; }
}
`;

if (typeof document !== "undefined" && !document.getElementById("ppc-style")) {
  const s = document.createElement("style");
  s.id = "ppc-style";
  s.innerHTML = INJECTED_CSS;
  document.head.appendChild(s);
}

const eventsOnDate = (events, date) =>
  events.filter((e) => moment(date).isBetween(e.start, e.end, "day", "[]"));
const dominantStatus = (events) => {
  const cnt = events.reduce((a, e) => {
    a[e.status] = (a[e.status] || 0) + 1;
    return a;
  }, {});
  let dom = null;
  Object.keys(cnt).forEach((k) => {
    if (!dom || cnt[k] > cnt[dom]) dom = k;
  });
  return dom || "Scheduled";
};

const PremiumEvent = ({ event }) => {
  const color = getStatusColor(event.status);
  const isLate = event.progress < 50 && moment(event.end).isBefore(moment());
  return (
    <div
      className="ppc-event"
      title={`${event.resource.product} • ${event.resource.orderNumber}`}
    >
      <div
        className="ppc-event-accent"
        style={{ background: isLate ? PALETTE.late : color }}
      />
      <div className="ppc-event-main">
        <div className="ppc-event-title">
          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              minWidth: 0,
            }}
          >
            {event.priority === "high" && (
              <span
                className="badge-pri"
                style={{
                  borderColor: "#fde68a",
                  background: "#fff",
                  color: "#92400e",
                }}
              >
                High
              </span>
            )}
            <div
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {event.title}
            </div>
          </div>
          <div style={{ fontSize: 12, color: PALETTE.slate }}>
            {event.resource.orderNumber}
          </div>
        </div>

        <div className="ppc-event-sub">
          <div
            style={{
              fontSize: 12,
              color: PALETTE.slate,
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {event.resource.product}
          </div>
          <div style={{ fontSize: 12, color: PALETTE.slate }}>
            {event.resource.machine}
          </div>
        </div>

        <div className="ppc-event-progress">
          <div className="ppc-track">
            <div
              className="ppc-fill"
              style={{
                width: `${event.progress}%`,
                background: isLate ? PALETTE.late : color,
              }}
            />
          </div>
          <div
            style={{
              width: 44,
              textAlign: "right",
              fontSize: 12,
              color: "#0f172a",
            }}
          >
            {event.progress}%
          </div>
        </div>
      </div>
    </div>
  );
};

const MonthHeader = ({ date, filteredEvents }) => {
  const dayEvents = eventsOnDate(filteredEvents, date);
  if (!dayEvents.length) {
    return (
      <div className="ppc-day-header">
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span style={{ fontWeight: 600, color: "#0f172a" }}>
            {moment(date).format("D")}
          </span>
          <small style={{ color: PALETTE.slate }}>
            {moment(date).format("ddd")}
          </small>
        </div>
      </div>
    );
  }
  const avg = Math.round(
    dayEvents.reduce((s, e) => s + (e.progress || 0), 0) / dayEvents.length,
  );
  const dom = dominantStatus(dayEvents);
  const color = getStatusColor(dom);
  const circ = 2 * Math.PI * 12;
  const dash = circ - (avg / 100) * circ;
  return (
    <div
      className="ppc-day-header"
      title={`${dayEvents.length} jobs — Avg ${avg}%`}
    >
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <div className="ppc-day-indicator">
          <svg width="36" height="36" viewBox="0 0 36 36">
            <circle cx="18" cy="18" r="12" fill="#f3f8ff" />
            <circle
              cx="18"
              cy="18"
              r="12"
              fill="none"
              stroke="#eef9ff"
              strokeWidth="3"
            />
            <circle
              cx="18"
              cy="18"
              r="12"
              fill="none"
              stroke={color}
              strokeWidth="3"
              strokeDasharray={circ}
              strokeDashoffset={dash}
              strokeLinecap="round"
              transform="rotate(-90 18 18)"
            />
            <text
              x="18"
              y="20"
              textAnchor="middle"
              fontSize="10"
              fontWeight="700"
              fill="#0f172a"
            >
              {dayEvents.length}
            </text>
          </svg>
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span style={{ fontWeight: 600, color: "#0f172a" }}>
            {moment(date).format("D")}
          </span>
          <small style={{ color: PALETTE.slate }}>
            {moment(date).format("ddd")}
          </small>
        </div>
      </div>
    </div>
  );
};

const ProductionCalendarPremium = ({ department = null }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [detailedCard, setDetailedCard] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  const [view, setView] = useState("month");
  const [selected, setSelected] = useState(null);
  const [hoverTooltip, setHoverTooltip] = useState({
    show: false,
    x: 0,
    y: 0,
    content: null,
  });
  const [filters, setFilters] = useState({
    machine: "All",
    status: "All",
    q: "",
  });

  const calendarRef = useRef(null);

  useEffect(() => {
    fetchProductionData();
  }, [department]);

  const fetchProductionData = async () => {
    setLoading(true);
    try {
      let url = `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/steps`;

      if (department && department !== "production") {
        url += `?department=${encodeURIComponent(department)}`;
      }

      const response = await axios.get(scopedProductionUrl(url), {
        headers: {
          accessToken: localStorage.getItem("accessToken"),
          "Content-Type": "application/json",
        },
      });

      console.log("Calendar data fetched:", response.data);

      if (response.data.success) {
        const calendarEvents = response.data.data.map((step) => {
          const normalizedStatus = step.status?.replace(/_/g, "");

          const progress = step.isFullyCompleted
            ? 100
            : normalizedStatus === "Completed"
            ? 100
            : normalizedStatus === "InProduction"
            ? 50
            : normalizedStatus === "QC"
            ? 75
            : 0;

          return {
            id: step.step_id,
            title: step.customerName || "Unknown Customer",
            start: step.plannedStart ? new Date(step.plannedStart) : new Date(),
            end: step.plannedFinish ? new Date(step.plannedFinish) : new Date(),
            status: normalizedStatus,
            progress: progress,
            priority: "normal",
            resource: {
              customer: step.customerName,
              product: step.productName || step.skuNumber,
              orderNumber: step.projectNumber,
              qtyPlanned: step.qtyRequired,
              qtyDone: 0,
              machine: step.machineName || step.machineCode,
              operator: step.operator || "Unassigned",
              department: step.department,
              sku: step.skuNumber,
              projectName: step.projectName,
              external_key: step.external_key,
              step_id: step.step_id,
            },
          };
        });

        console.log("Calendar events created:", calendarEvents.length);
        setEvents(calendarEvents);
      }
    } catch (error) {
      console.error("Failed to fetch calendar data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCardDetails = async (externalkey) => {
    setModalLoading(true);
    try {
      const response = await axios.get(
        scopedProductionUrl(`${process.env.REACT_APP_NODEJS_SERVER_URL}/production/order-details?external_key=${externalkey}`),
        {
          headers: {
            accessToken: localStorage.getItem("accessToken"),
            "Content-Type": "application/json",
          },
        },
      );

      console.log("Calendar - Detailed card response:", response.data);

      if (
        response.data.success &&
        response.data.data &&
        response.data.data.length > 0
      ) {
        setDetailedCard(response.data.data[0]);
      }
    } catch (error) {
      console.error("Failed to fetch card details:", error);
    } finally {
      setModalLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedCard(null);
    setDetailedCard(null);
  };

  const handlePatchCard = (cardId, updates) => {
    console.log("Card updated:", cardId, updates);
    fetchProductionData();
  };

  const machines = useMemo(
    () => [
      "All",
      ...Array.from(new Set(events.map((e) => e.resource.machine))).filter(
        Boolean,
      ),
    ],
    [events],
  );
  const statuses = ["All", "Scheduled", "InProduction", "QC", "Completed"];

  const filteredEvents = useMemo(() => {
    const q = filters.q.trim().toLowerCase();
    return events.filter((e) => {
      if (filters.machine !== "All" && e.resource.machine !== filters.machine)
        return false;
      if (filters.status !== "All" && e.status !== filters.status) return false;
      if (
        q.length &&
        !(
          (e.title || "").toLowerCase().includes(q) ||
          (e.resource.orderNumber || "").toLowerCase().includes(q) ||
          (e.resource.product || "").toLowerCase().includes(q)
        )
      )
        return false;
      return true;
    });
  }, [events, filters]);

  const stats = useMemo(
    () => ({
      total: filteredEvents.length,
      scheduled: filteredEvents.filter((e) => e.status === "Scheduled").length,
      active: filteredEvents.filter((e) => e.status === "InProduction").length,
      qc: filteredEvents.filter((e) => e.status === "QC").length,
      done: filteredEvents.filter((e) => e.status === "Completed").length,
    }),
    [filteredEvents],
  );

  const eventStyleGetter = () => ({
    style: {
      backgroundColor: PALETTE.bg,
      border: `1px solid ${PALETTE.border}`,
      padding: 0,
    },
  });

  const onEventMouseEnter = (e, rawEvent) => {
    const x = rawEvent.clientX + 10;
    const y = rawEvent.clientY + 8;
    setHoverTooltip({
      show: true,
      x,
      y,
      content: e,
    });
  };
  const onEventMouseLeave = () =>
    setHoverTooltip({ show: false, x: 0, y: 0, content: null });

  const EventWrapper = ({ event }) => {
    return (
      <div
        onMouseEnter={(ev) => onEventMouseEnter(event, ev)}
        onMouseLeave={onEventMouseLeave}
      >
        <PremiumEvent event={event} />
      </div>
    );
  };

  const MonthDateHeader = ({ date, label }) => (
    <MonthHeader date={date} filteredEvents={filteredEvents} />
  );

  const onSelectEvent = (ev) => {
    console.log("Calendar event clicked:", ev);

    const cardData = {
      id: ev.resource.external_key,
      stepid: ev.resource.step_id,
      orderNumber: ev.resource.orderNumber,
      projectName: ev.resource.projectName,
      productName: ev.resource.product,
      sku: ev.resource.sku,
      customer: ev.resource.customer,
      machine: ev.resource.machine,
      operator: ev.resource.operator,
      qtyPlanned: ev.resource.qtyPlanned,
      qtyRequired: ev.resource.qtyPlanned,
      status: ev.status,
      department: ev.resource.department,
      plannedStart: ev.start,
      plannedFinish: ev.end,
      isStep: true,
      orderDbId: ev.resource.orderDbId,
      itemId: ev.resource.itemId,
      pwoid: ev.resource.pwoid,
      externalkey: ev.resource.external_key,
    };

    setSelectedCard(cardData);
    setShowModal(true);
    fetchCardDetails(ev.resource.external_key);
  };

  const reset = () => setFilters({ machine: "All", status: "All", q: "" });

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setSelected(null);
        setHoverTooltip({ show: false, x: 0, y: 0, content: null });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const TooltipBox = ({ content }) => {
    if (!content) return null;
    const r = content.resource || {};
    return (
      <div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
          }}
        >
          <div style={{ fontWeight: 700, color: "#0f172a" }}>{r.customer}</div>
          <div style={{ fontSize: 12, color: PALETTE.slate }}>
            {content.resource.orderNumber}
          </div>
        </div>
        <div style={{ marginTop: 8, color: PALETTE.slate }}>{r.product}</div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: 10,
            gap: 8,
          }}
        >
          <div style={{ fontSize: 12, color: PALETTE.slate }}>
            <div>
              <strong>{content.progress}%</strong> complete
            </div>
            <div style={{ color: PALETTE.slate, marginTop: 6 }}>
              {r.qtyDone}/{r.qtyPlanned}
            </div>
          </div>
          <div
            style={{ textAlign: "right", fontSize: 12, color: PALETTE.slate }}
          >
            <div>{r.machine}</div>
            <div style={{ marginTop: 6 }}>{r.operator}</div>
          </div>
        </div>
      </div>
    );
  };

  const components = {
    event: EventWrapper,
    month: { dateHeader: MonthDateHeader },
  };

  if (loading) {
    return (
      <div className="ppc-root">
        <div style={{ padding: 60, textAlign: "center" }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading calendar...</span>
          </div>
          <p style={{ marginTop: 16, color: PALETTE.slate }}>
            Loading production schedule...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="ppc-root">
      <div className="ppc-header">
        <div>
          <div className="ppc-title">
            Production Calendar
            {department && department !== "production" && (
              <small
                style={{ color: PALETTE.slate, fontWeight: 500 }}
              >{` · ${department}`}</small>
            )}
          </div>
          <div style={{ marginTop: 8, color: PALETTE.slate, fontSize: 13 }}>
            Real-time production scheduling · {events.length} total jobs
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <div className="ppc-stats">
            <div className="ppc-stat">
              <div className="value">{stats.total}</div>
              <div style={{ fontSize: 12, color: PALETTE.slate }}>Total</div>
            </div>
            <div className="ppc-stat">
              <div className="value">{stats.active}</div>
              <div style={{ fontSize: 12, color: PALETTE.slate }}>Active</div>
            </div>
            <div className="ppc-stat">
              <div className="value">{stats.qc}</div>
              <div style={{ fontSize: 12, color: PALETTE.slate }}>QC</div>
            </div>
            <div className="ppc-stat">
              <div className="value" style={{ color: PALETTE.completed }}>
                {stats.done}
              </div>
              <div style={{ fontSize: 12, color: PALETTE.slate }}>Done</div>
            </div>
          </div>
        </div>
      </div>

      <div className="ppc-controls mb-2">
        <div className="btn-group" role="group" aria-label="view toggles">
          <button
            className={`btn btn-sm btn-outline-secondary ppc-btn ${
              view === "month" ? "active" : ""
            }`}
            onClick={() => setView("month")}
          >
            Month
          </button>
          <button
            className={`btn btn-sm btn-outline-secondary ppc-btn ${
              view === "week" ? "active" : ""
            }`}
            onClick={() => setView("week")}
          >
            Week
          </button>
          <button
            className={`btn btn-sm btn-outline-secondary ppc-btn ${
              view === "day" ? "active" : ""
            }`}
            onClick={() => setView("day")}
          >
            Day
          </button>
        </div>

        <input
          className="form-control form-control-sm ppc-search"
          placeholder="Search order, product, customer..."
          value={filters.q}
          onChange={(e) => setFilters((s) => ({ ...s, q: e.target.value }))}
        />

        <select
          className="form-select form-select-sm"
          style={{ width: 160 }}
          value={filters.machine}
          onChange={(e) =>
            setFilters((s) => ({ ...s, machine: e.target.value }))
          }
        >
          {machines.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>

        <select
          className="form-select form-select-sm"
          style={{ width: 140 }}
          value={filters.status}
          onChange={(e) =>
            setFilters((s) => ({ ...s, status: e.target.value }))
          }
        >
          {statuses.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>

        <button className="btn btn-sm btn-outline-primary" onClick={reset}>
          Reset
        </button>
      </div>

      {/* Calendar */}
      <div className="ppc-calendar-wrap">
        <Calendar
          ref={calendarRef}
          localizer={localizer}
          events={filteredEvents}
          startAccessor="start"
          endAccessor="end"
          style={{ height: "74vh" }}
          views={["month", "week", "day", "agenda"]}
          view={view}
          onView={(v) => setView(v)}
          components={components}
          eventPropGetter={eventStyleGetter}
          onSelectEvent={onSelectEvent}
          popup
          tooltipAccessor={() => ""}
          dayPropGetter={(date) =>
            moment(date).isSame(moment(), "day")
              ? { style: { backgroundColor: "#f3f8ff" } }
              : {}
          }
        />
      </div>

      {hoverTooltip.show && hoverTooltip.content && (
        <div
          className="ppc-tooltip"
          style={{ left: hoverTooltip.x, top: hoverTooltip.y }}
        >
          <TooltipBox content={hoverTooltip.content} />
        </div>
      )}

      {selected && (
        <div className="ppc-slide">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 12,
            }}
          >
            <div>
              <div style={{ fontWeight: 800, fontSize: 16, color: "#0f172a" }}>
                {selected.resource.orderNumber}
              </div>
              <div style={{ fontSize: 13, color: PALETTE.slate }}>
                {selected.resource.customer} · {selected.resource.product}
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div
                style={{
                  color: getStatusColor(selected.status),
                  fontWeight: 700,
                }}
              >
                {selected.status}
              </div>
              <div style={{ fontSize: 12, color: PALETTE.slate }}>
                {moment(selected.start).format("DD MMM YYYY")} —{" "}
                {moment(selected.end).format("DD MMM YYYY")}
              </div>
            </div>
          </div>

          <div
            style={{
              borderTop: `1px solid ${PALETTE.border}`,
              paddingTop: 12,
              marginBottom: 12,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 8,
              }}
            >
              <div>
                <small className="text-muted">Machine</small>
                <div style={{ fontWeight: 700, color: "#0f172a" }}>
                  {selected.resource.machine}
                </div>
              </div>
              <div>
                <small className="text-muted">Operator</small>
                <div style={{ fontWeight: 700, color: "#0f172a" }}>
                  {selected.resource.operator}
                </div>
              </div>
            </div>

            <div style={{ marginTop: 6 }}>
              <small className="text-muted">Quantity</small>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: 6,
                }}
              >
                <div>{selected.resource.qtyDone} done</div>
                <div>{selected.resource.qtyPlanned} planned</div>
              </div>
              <div style={{ marginTop: 8 }}>
                <div className="ppc-track" style={{ height: 12 }}>
                  <div
                    className="ppc-fill"
                    style={{
                      width: `${selected.progress}%`,
                      background: getStatusColor(selected.status),
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <button
              className="btn btn-primary btn-sm w-50"
              onClick={() => alert("Open order (placeholder)")}
            >
              Open Order
            </button>
            <button
              className="btn btn-outline-secondary btn-sm w-50"
              onClick={() => alert("Reschedule (placeholder)")}
            >
              Reschedule
            </button>
          </div>

          <div style={{ marginTop: 12, color: PALETTE.slate, fontSize: 13 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Notes</div>
            <div
              style={{
                background: "#f8fafc",
                borderRadius: 8,
                padding: 8,
                minHeight: 60,
              }}
            >
              No notes yet. Add comments or attach production files later.
            </div>
          </div>

          <div
            style={{
              marginTop: 16,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <button
              className="btn btn-sm btn-link"
              onClick={() => setSelected(null)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {showModal && selectedCard && (
        <JobCardModal
          open={showModal}
          onClose={handleCloseModal}
          card={selectedCard}
          detailedCard={detailedCard}
          modalLoading={modalLoading}
          onPatchCard={handlePatchCard}
          orderMeta={detailedCard?.meta}
          orderCosting={detailedCard?.costing}
          orderCharges={detailedCard?.charges}
          siblingsAll={[]}
          siblingsSameLane={[]}
        />
      )}
    </div>
  );
};

export default ProductionCalendarPremium;
